import { z } from 'zod';
import type { Consts, ConstDefinitionData } from '../../containers/xml-editing/services/LVGLSchemaService';
import { isAbsoluteFolderPath } from '../../utils/path';

export const IMAGE_CONVERT_COLOR_FORMATS = ['rgb565', 'rgb565a8', 'rgb888', 'xrgb8888', 'argb8888', 'a8'] as const;

export type ImageConvertColorFormat = (typeof IMAGE_CONVERT_COLOR_FORMATS)[number];

export const ImageConvertColorFormat = z.enum(IMAGE_CONVERT_COLOR_FORMATS);

const OptionalImageConvertColorFormat = z.preprocess((value: unknown): unknown => {
    if (typeof value !== 'string') return value;

    const normalized: string = value.trim().toLowerCase();
    return normalized === '' ? undefined : normalized;
}, ImageConvertColorFormat.optional());

export const ImageConvert = z.object({
    src: z.string(),
    dest: z.string(),
    width: z.string().optional(),
    height: z.string().optional(),
    scale: z.string().optional(),
    color_format: OptionalImageConvertColorFormat
});

export type ImageConvert = z.infer<typeof ImageConvert>;

export type ImageConvertSourceSize = Readonly<{
    width: number;
    height: number;
}>;

export type ResolvedImageConvertSize = Readonly<{
    width: number;
    height: number;
}>;

export type ImageConvertPathValidationIssue = Readonly<{
    attribute: 'src' | 'dest';
    message: string;
}>;

export type ImageConvertBatchValidationIssue = Readonly<
    ImageConvertPathValidationIssue & {
        index: number;
        existingIndex?: number;
    }
>;

const DEFAULT_IMAGE_CONVERT_COLOR_FORMAT: ImageConvertColorFormat = 'rgb565';
const IMAGE_CONVERT_COLOR_FORMAT_SET: ReadonlySet<string> = new Set<string>(IMAGE_CONVERT_COLOR_FORMATS);

const POSITIVE_INTEGER_PATTERN: RegExp = /^[1-9]\d*$/;
const POSITIVE_PERCENT_PATTERN: RegExp = /^(\d+(?:\.\d+)?)%$/;

const isImageConvertColorFormat = (value: string): value is ImageConvertColorFormat =>
    IMAGE_CONVERT_COLOR_FORMAT_SET.has(value);

export const normalizeImageConvertColorFormat = (value: string | undefined): ImageConvertColorFormat => {
    const normalized: string = (value ?? DEFAULT_IMAGE_CONVERT_COLOR_FORMAT).trim().toLowerCase();

    if (isImageConvertColorFormat(normalized)) return normalized;

    throw new Error(
        `Unsupported <convert> color_format '${value}'. Supported: ${IMAGE_CONVERT_COLOR_FORMATS.join(', ')}.`
    );
};

const classifyPixel = (value: number, bits: number): number => {
    const step: number = 1 << (8 - bits);
    return Math.max(0, Math.round(value / step) * step);
};

const quantizeRgb565Channel = (value: number, bits: 5 | 6): number => {
    const classified: number = classifyPixel(value, bits);
    return Math.min(classified, bits === 5 ? 0xf8 : 0xfc);
};

export const applyImageConvertColorFormat = (
    pixels: Uint8Array | Uint8ClampedArray,
    colorFormat: ImageConvertColorFormat
): Uint8ClampedArray => {
    if (pixels.length % 4 !== 0) {
        throw new Error(`RGBA pixel buffer length must be divisible by 4, received ${pixels.length}.`);
    }

    const transformed: Uint8ClampedArray = new Uint8ClampedArray(pixels.length);

    for (let index: number = 0; index < pixels.length; index += 4) {
        const sourceRed: number = pixels[index];
        const sourceGreen: number = pixels[index + 1];
        const sourceBlue: number = pixels[index + 2];
        const sourceAlpha: number = pixels[index + 3];

        switch (colorFormat) {
            case 'rgb565':
                transformed[index] = quantizeRgb565Channel(sourceRed, 5);
                transformed[index + 1] = quantizeRgb565Channel(sourceGreen, 6);
                transformed[index + 2] = quantizeRgb565Channel(sourceBlue, 5);
                transformed[index + 3] = 0xff;
                break;
            case 'rgb565a8':
                transformed[index] = quantizeRgb565Channel(sourceRed, 5);
                transformed[index + 1] = quantizeRgb565Channel(sourceGreen, 6);
                transformed[index + 2] = quantizeRgb565Channel(sourceBlue, 5);
                transformed[index + 3] = sourceAlpha;
                break;
            case 'rgb888':
            case 'xrgb8888':
                transformed[index] = sourceRed;
                transformed[index + 1] = sourceGreen;
                transformed[index + 2] = sourceBlue;
                transformed[index + 3] = 0xff;
                break;
            case 'argb8888':
                transformed[index] = sourceRed;
                transformed[index + 1] = sourceGreen;
                transformed[index + 2] = sourceBlue;
                transformed[index + 3] = sourceAlpha;
                break;
            case 'a8':
                transformed[index] = 0;
                transformed[index + 1] = 0;
                transformed[index + 2] = 0;
                transformed[index + 3] = sourceAlpha;
                break;
        }
    }

    return transformed;
};

export const normalizeProjectRelativeConvertPath = (value: string): string =>
    value
        .trim()
        .replace(/\\/g, '/')
        .replace(/^\.\/+/, '');

const hasParentTraversal = (value: string): boolean =>
    normalizeProjectRelativeConvertPath(value).split('/').includes('..');

const validateRelativeConvertPath = (
    attribute: 'src' | 'dest',
    value: string,
    requirePng: boolean
): ImageConvertPathValidationIssue[] => {
    const issues: ImageConvertPathValidationIssue[] = [];
    const trimmed: string = value.trim();
    const normalized: string = normalizeProjectRelativeConvertPath(value);

    if (!trimmed) {
        issues.push({ attribute, message: `<convert> '${attribute}' must not be empty.` });
        return issues;
    }

    if (isAbsoluteFolderPath(trimmed)) {
        issues.push({ attribute, message: `<convert> '${attribute}' must be project-relative.` });
    }

    if (hasParentTraversal(trimmed)) {
        issues.push({ attribute, message: `<convert> '${attribute}' must not contain '..'.` });
    }

    if (requirePng && !normalized.toLowerCase().endsWith('.png')) {
        issues.push({ attribute, message: `<convert> 'dest' must be a .png path.` });
    }

    return issues;
};

export const validateImageConvertPaths = (
    convert: Pick<ImageConvert, 'src' | 'dest'>
): ImageConvertPathValidationIssue[] => {
    const issues: ImageConvertPathValidationIssue[] = [
        ...validateRelativeConvertPath('src', convert.src, false),
        ...validateRelativeConvertPath('dest', convert.dest, true)
    ];

    const normalizedSrc: string = normalizeProjectRelativeConvertPath(convert.src);
    const normalizedDest: string = normalizeProjectRelativeConvertPath(convert.dest);

    if (normalizedSrc && normalizedDest && normalizedSrc === normalizedDest) {
        issues.push({ attribute: 'dest', message: `<convert> 'dest' must be different from 'src'.` });
    }

    return issues;
};

export const assertImageConvertPaths = (convert: Pick<ImageConvert, 'src' | 'dest'>): void => {
    const issues: ImageConvertPathValidationIssue[] = validateImageConvertPaths(convert);
    if (issues.length > 0)
        throw new Error(issues.map((issue: ImageConvertPathValidationIssue) => issue.message).join(' '));
};

export const validateImageConvertBatch = (
    converts: readonly Pick<ImageConvert, 'src' | 'dest'>[]
): ImageConvertBatchValidationIssue[] => {
    const issues: ImageConvertBatchValidationIssue[] = [];
    const destinationsByPath: Map<string, number> = new Map<string, number>();

    converts.forEach((convert: Pick<ImageConvert, 'src' | 'dest'>, index: number): void => {
        validateImageConvertPaths(convert).forEach((issue: ImageConvertPathValidationIssue): void => {
            issues.push({ ...issue, index });
        });

        const normalizedDest: string = normalizeProjectRelativeConvertPath(convert.dest);
        if (!normalizedDest) return;

        const existingIndex: number | undefined = destinationsByPath.get(normalizedDest);
        if (existingIndex !== undefined) {
            issues.push({
                index,
                existingIndex,
                attribute: 'dest',
                message: `<convert> 'dest' collides with another conversion destination.`
            });
            return;
        }

        destinationsByPath.set(normalizedDest, index);
    });

    return issues;
};

export const assertImageConvertBatch = (converts: readonly Pick<ImageConvert, 'src' | 'dest'>[]): void => {
    const issues: ImageConvertBatchValidationIssue[] = validateImageConvertBatch(converts);
    if (issues.length === 0) return;

    throw new Error(
        issues
            .map((issue: ImageConvertBatchValidationIssue): string => `<convert>[${issue.index}]: ${issue.message}`)
            .join(' ')
    );
};

const resolveConstBackedValue = (
    rawValue: string,
    consts: Consts,
    attributeName: 'width' | 'height' | 'scale'
): string => {
    const trimmed: string = rawValue.trim();
    if (!trimmed.startsWith('#')) return trimmed;

    const constName: string = trimmed.slice(1).trim();
    const matchingConst: ConstDefinitionData | undefined = consts[constName]?.[0].def;
    if (!matchingConst) {
        throw new Error(`<convert> '${attributeName}' references missing globals.xml const '#${constName}'.`);
    }

    return matchingConst.value.trim();
};

const parsePositiveIntegerDimension = (rawValue: string, attributeName: 'width' | 'height', consts: Consts): number => {
    const resolved: string = resolveConstBackedValue(rawValue, consts, attributeName);

    if (!POSITIVE_INTEGER_PATTERN.test(resolved)) {
        throw new Error(
            `<convert> '${attributeName}' must be a positive integer or a #const with a positive integer value.`
        );
    }

    return Number(resolved);
};

const parseScaleFactor = (rawValue: string, consts: Consts): number => {
    const resolved: string = resolveConstBackedValue(rawValue, consts, 'scale');
    const match: RegExpExecArray | null = POSITIVE_PERCENT_PATTERN.exec(resolved);

    if (!match) {
        throw new Error(`<convert> 'scale' must be a positive percent value like '50%' or a #const with that value.`);
    }

    const percent: number = Number(match[1]);
    if (!Number.isFinite(percent) || percent <= 0) {
        throw new Error(`<convert> 'scale' must be greater than 0%.`);
    }

    return percent / 100;
};

const assertValidSourceSize = (sourceSize: ImageConvertSourceSize): void => {
    if (
        !Number.isFinite(sourceSize.width) ||
        !Number.isFinite(sourceSize.height) ||
        sourceSize.width <= 0 ||
        sourceSize.height <= 0
    ) {
        throw new Error(`Cannot resize image with invalid source size ${sourceSize.width}x${sourceSize.height}.`);
    }
};

export const resolveImageConvertSize = (
    convert: Pick<ImageConvert, 'width' | 'height' | 'scale'>,
    consts: Consts,
    sourceSize: ImageConvertSourceSize
): ResolvedImageConvertSize => {
    assertValidSourceSize(sourceSize);

    if (convert.scale !== undefined && (convert.width !== undefined || convert.height !== undefined)) {
        throw new Error(`<convert> 'scale' cannot be combined with 'width' or 'height'.`);
    }

    if (convert.scale !== undefined) {
        const scaleFactor: number = parseScaleFactor(convert.scale, consts);
        return {
            width: Math.max(1, Math.round(sourceSize.width * scaleFactor)),
            height: Math.max(1, Math.round(sourceSize.height * scaleFactor))
        };
    }

    const width: number | undefined =
        convert.width !== undefined ? parsePositiveIntegerDimension(convert.width, 'width', consts) : undefined;
    const height: number | undefined =
        convert.height !== undefined ? parsePositiveIntegerDimension(convert.height, 'height', consts) : undefined;

    if (width !== undefined && height !== undefined) return { width, height };
    if (width !== undefined)
        return { width, height: Math.max(1, Math.round((sourceSize.height * width) / sourceSize.width)) };
    if (height !== undefined)
        return { width: Math.max(1, Math.round((sourceSize.width * height) / sourceSize.height)), height };

    return {
        width: Math.round(sourceSize.width),
        height: Math.round(sourceSize.height)
    };
};
